import { Button, Flex, Grid, Modal, Spin } from "antd"
import React, { useEffect, useRef, useState } from "react"
import { $t } from "~/i18n.js"
import { colors } from "~/theme/colors.js"
import {
  canvasPoint,
  drawSchematic,
  getImageRect,
  toCanvasPoint,
  toImagePosition,
  useDamageSchematic,
} from "./damageSchematic.js"

export const DamagePositionModal = ({ material, initialPosition, onSave, onClose }) => {
  const screens = Grid.useBreakpoint()
  const wrapperRef = useRef(null)
  const canvasRef = useRef(null)
  const imageRectRef = useRef(null)
  const { backgroundRef, backgroundLoaded } = useDamageSchematic(material)
  const isMobile = !screens.md

  const [position, setPosition] = useState(initialPosition || null)
  const isChanged =
    position?.x !== initialPosition?.x || position?.y !== initialPosition?.y
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const canvas = canvasRef.current
    const wrapper = wrapperRef.current
    if (!canvas || !wrapper) return

    const ctx = canvas.getContext("2d")

    const draw = () => {
      // clientWidth/Height: getBoundingClientRect is affected by antd's Modal zoom animation
      const width = wrapper.clientWidth
      const height = wrapper.clientHeight
      if (!width || !height) return

      canvas.width = width
      canvas.height = height
      ctx.clearRect(0, 0, width, height)

      const rect = getImageRect(backgroundRef.current, width, height)
      imageRectRef.current = rect
      drawSchematic(ctx, backgroundRef.current, rect)

      if (position) {
        const point = toCanvasPoint(rect, position)
        ctx.beginPath()
        ctx.arc(point.x, point.y, 16, 0, 2 * Math.PI)
        ctx.fillStyle = colors.orange
        ctx.fill()
      }
    }

    draw()

    const observer = new ResizeObserver(draw)
    observer.observe(wrapper)

    return () => {
      observer.disconnect()
    }
  }, [position, backgroundLoaded])

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave(position)
    } finally {
      setSaving(false)
    }
  }

  const loading = !backgroundLoaded

  const handleCanvasClick = (event) => {
    if (loading || !imageRectRef.current) return
    const clicked = toImagePosition(imageRectRef.current, canvasPoint(event, canvasRef.current))
    if (clicked) setPosition(clicked)
  }

  return (
    <Modal
      title={$t("Positie op schadekaart")}
      open={true}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {$t("Annuleren")}
        </Button>,
        <Button key="clear" danger disabled={!position} onClick={() => setPosition(null)}>
          {$t("Positie verwijderen")}
        </Button>,
        <Button key="save" type="primary" loading={saving} disabled={!isChanged} onClick={handleSave}>
          {initialPosition ? $t("Positie wijzigen") : $t("Opslaan")}
        </Button>,
      ]}
      width={isMobile ? "100%" : 720}
    >
      <p>{$t("Klik op de schadekaart om de positie aan te geven of een bestaande positie te verplaatsen.")}</p>
      <div
        ref={wrapperRef}
        style={{ width: "100%", height: isMobile ? "60vh" : 600, position: "relative" }}
      >
        <canvas
          ref={canvasRef}
          style={{ display: "block", width: "100%", height: "100%", cursor: loading ? "progress" : "crosshair" }}
          onClick={handleCanvasClick}
        />
        {loading && (
          <Flex
            align="center"
            justify="center"
            style={{ position: "absolute", inset: 0 }}
          >
            <Spin />
          </Flex>
        )}
      </div>
    </Modal>
  )
}
